// Service Worker Registration for Biztimize PWA
// Based on CRA 5's built-in service-worker registration pattern

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
  window.location.hostname === '[::1]' ||
  window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/)
);

export function register(config) {
  if (!('serviceWorker' in navigator)) return;

  // Only register on tenant subdomains, not the marketing site (biztimize.com)
  const host = window.location.hostname;
  if (host === 'biztimize.com' || host === 'www.biztimize.com') return;

  const publicUrl = new URL(process.env.PUBLIC_URL, window.location.href);
  if (publicUrl.origin !== window.location.origin) return;

  // In DEVELOPMENT, register the push-only worker from `public/`.
  //
  // CRA does not compile `src/service-worker.js` under `npm start`, so this
  // branch used to be skipped entirely — leaving dev with no service worker,
  // hence no `push` event, hence no OS notification however well the backend
  // sent one. `dev-service-worker.js` carries push and nothing else: no
  // precache and no fetch handler, because caching a dev server fights the hot
  // reloader. Push itself needs a secure context, which `*.localhost` is in
  // Chrome and Edge (Firefox does not agree — test the tray in Chrome).
  if (process.env.NODE_ENV !== 'production') {
    window.addEventListener('load', () => {
      navigator.serviceWorker
        .register(`${process.env.PUBLIC_URL}/dev-service-worker.js`)
        .then(() => console.log('[dev] Push service worker registered.'))
        .catch((error) => console.warn('[dev] Push SW registration failed:', error));
    });
    return;
  }

  window.addEventListener('load', () => {
    const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;

    if (isLocalhost) {
      checkValidServiceWorker(swUrl, config);
      navigator.serviceWorker.ready.then(() => {
        console.log('Biztimize PWA is being served cache-first by a service worker.');
      });
    } else {
      registerValidSW(swUrl, config);
    }
  });
}

function registerValidSW(swUrl, config) {
  navigator.serviceWorker
    .register(swUrl)
    .then((registration) => {
      registration.onupdatefound = () => {
        const installingWorker = registration.installing;
        if (installingWorker == null) return;

        installingWorker.onstatechange = () => {
          if (installingWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              // New content available — notify the app
              console.log('New Biztimize version available.');
              if (config && config.onUpdate) {
                config.onUpdate(registration);
              }
            } else {
              // Content cached for the first time — offline ready
              console.log('Biztimize is cached for offline use.');
              if (config && config.onSuccess) {
                config.onSuccess(registration);
              }
            }
          }
        };
      };
    })
    .catch((error) => {
      console.error('Error during service worker registration:', error);
    });
}

function checkValidServiceWorker(swUrl, config) {
  fetch(swUrl, { headers: { 'Service-Worker': 'script' } })
    .then((response) => {
      const contentType = response.headers.get('content-type');
      if (response.status === 404 || (contentType != null && contentType.indexOf('javascript') === -1)) {
        navigator.serviceWorker.ready.then((registration) => {
          registration.unregister().then(() => window.location.reload());
        });
      } else {
        registerValidSW(swUrl, config);
      }
    })
    .catch(() => {
      console.log('No internet connection found. App is running in offline mode.');
    });
}

export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => registration.unregister())
      .catch((error) => console.error(error.message));
  }
}
